import React from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  AlertTitle,
  Collapse,
  IconButton,
  Snackbar,
  Slide,
  Box,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

/**
 * MuiAlert - Alert/notification component
 *
 * Von Restorff Effect: Different colors for different severity levels
 * Jakob's Law: Standard alert patterns with familiar icons
 */

const StyledAlert = styled(Alert)(({ theme, variant }) => ({
  borderRadius: 8,
  alignItems: 'flex-start',
  ...(variant === 'filled' && {
    color: '#fff',
  }),
  '& .MuiAlert-icon': {
    marginTop: 2,
  },
  '& .MuiAlert-message': {
    padding: '4px 0',
  },
  '& .MuiAlert-action': {
    paddingTop: 0,
    alignItems: 'flex-start',
  },
}));

const severityIcons = {
  success: CheckCircleOutlineIcon,
  error: ErrorOutlineIcon,
  warning: WarningAmberIcon,
  info: InfoOutlinedIcon,
};

function SlideTransition(props) {
  return <Slide {...props} direction="down" />;
}

// Inline Alert Component
const InlineAlert = ({
  severity = 'info',
  variant = 'standard',
  title,
  children,
  icon,
  action,
  onClose,
  closable = false,
  show = true,
  sx = {},
  ...props
}) => {
  const [isVisible, setIsVisible] = React.useState(show);

  React.useEffect(() => {
    setIsVisible(show);
  }, [show]);

  const handleClose = (event) => {
    setIsVisible(false);
    onClose?.(event);
  };

  const IconComponent = severityIcons[severity];

  const closeButton = closable ? (
    <IconButton
      aria-label="close"
      color="inherit"
      size="small"
      onClick={handleClose}
    >
      <CloseIcon fontSize="small" />
    </IconButton>
  ) : null;

  return (
    <Collapse in={isVisible}>
      <StyledAlert
        severity={severity}
        variant={variant}
        icon={icon !== undefined ? icon : <IconComponent />}
        action={action || closeButton}
        sx={sx}
        {...props}
      >
        {title && <AlertTitle sx={{ fontWeight: 600 }}>{title}</AlertTitle>}
        {children}
      </StyledAlert>
    </Collapse>
  );
};

// Toast/Snackbar Alert Component
const ToastAlert = ({
  open,
  onClose,
  severity = 'info',
  variant = 'filled',
  title,
  message,
  autoHideDuration = 5000,
  anchorOrigin = { vertical: 'top', horizontal: 'right' },
  TransitionComponent = SlideTransition,
  closable = true,
  sx = {},
  ...props
}) => {
  return (
    <Snackbar
      open={open}
      autoHideDuration={autoHideDuration}
      onClose={(event, reason) => {
        if (reason === 'clickaway') return;
        onClose?.(event, reason);
      }}
      anchorOrigin={anchorOrigin}
      TransitionComponent={TransitionComponent}
      {...props}
    >
      <StyledAlert
        severity={severity}
        variant={variant}
        onClose={closable ? onClose : undefined}
        sx={{ minWidth: 280, boxShadow: 3, ...sx }}
      >
        {title && <AlertTitle sx={{ fontWeight: 600 }}>{title}</AlertTitle>}
        {message}
      </StyledAlert>
    </Snackbar>
  );
};

// Main MuiAlert component with both modes
const MuiAlert = ({
  mode = 'inline',
  ...props
}) => {
  if (mode === 'toast') {
    return <ToastAlert {...props} />;
  }
  return <InlineAlert {...props} />;
};

// Export sub-components for direct use
MuiAlert.Inline = InlineAlert;
MuiAlert.Toast = ToastAlert;

// Custom hook for managing toast alerts
export const useToast = () => {
  const [toasts, setToasts] = React.useState([]);

  const showToast = React.useCallback((options) => {
    const id = Date.now();
    const toast = { id, ...options };
    setToasts((prev) => [...prev, toast]);
    return id;
  }, []);

  const hideToast = React.useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const success = React.useCallback((message, options = {}) => {
    return showToast({ severity: 'success', message, ...options });
  }, [showToast]);

  const error = React.useCallback((message, options = {}) => {
    return showToast({ severity: 'error', message, ...options });
  }, [showToast]);

  const warning = React.useCallback((message, options = {}) => {
    return showToast({ severity: 'warning', message, ...options });
  }, [showToast]);

  const info = React.useCallback((message, options = {}) => {
    return showToast({ severity: 'info', message, ...options });
  }, [showToast]);

  const ToastContainer = React.useCallback(() => (
    <>
      {toasts.map((toast) => (
        <MuiAlert.Toast
          key={toast.id}
          open={true}
          onClose={() => hideToast(toast.id)}
          {...toast}
        />
      ))}
    </>
  ), [toasts, hideToast]);

  return {
    showToast,
    hideToast,
    success,
    error,
    warning,
    info,
    ToastContainer,
    toasts,
  };
};

MuiAlert.propTypes = {
  /** Alert mode - inline or toast */
  mode: PropTypes.oneOf(['inline', 'toast']),
};

InlineAlert.propTypes = {
  /** Severity level */
  severity: PropTypes.oneOf(['success', 'error', 'warning', 'info']),
  /** Visual variant */
  variant: PropTypes.oneOf(['standard', 'filled', 'outlined']),
  /** Alert title */
  title: PropTypes.node,
  /** Alert content */
  children: PropTypes.node,
  /** Custom icon */
  icon: PropTypes.node,
  /** Custom action */
  action: PropTypes.node,
  /** Close callback */
  onClose: PropTypes.func,
  /** Show close button */
  closable: PropTypes.bool,
  /** Control visibility */
  show: PropTypes.bool,
  /** Custom styles */
  sx: PropTypes.object,
};

ToastAlert.propTypes = {
  /** Control visibility */
  open: PropTypes.bool.isRequired,
  /** Close callback */
  onClose: PropTypes.func,
  /** Severity level */
  severity: PropTypes.oneOf(['success', 'error', 'warning', 'info']),
  /** Visual variant */
  variant: PropTypes.oneOf(['standard', 'filled', 'outlined']),
  /** Alert title */
  title: PropTypes.node,
  /** Alert message */
  message: PropTypes.node,
  /** Auto hide duration in ms */
  autoHideDuration: PropTypes.number,
  /** Snackbar position */
  anchorOrigin: PropTypes.shape({
    vertical: PropTypes.oneOf(['top', 'bottom']),
    horizontal: PropTypes.oneOf(['left', 'center', 'right']),
  }),
  /** Transition component */
  TransitionComponent: PropTypes.elementType,
  /** Show close button */
  closable: PropTypes.bool,
  /** Custom styles */
  sx: PropTypes.object,
};

export default MuiAlert;
